import React, { useMemo } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { flexRender } from '@tanstack/react-table'
import { cn } from '@/lib/utils'

function SortableRow({ row, onRowClick }) {
  const isDraft = row.original.id === 'draft'
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: row.id, disabled: isDraft })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      className={cn(
        'data-[state=selected]:border-primary',
        isDragging && 'opacity-50 shadow-lg',
      )}
      onClick={() => onRowClick(row)}
      data-state={row.getIsSelected() && 'selected'}
    >
      {row.getVisibleCells().map((cell) => (
        <TableCell
          key={cell.id}
          className={cell.column.id === 'drag-handle' || cell.column.id === 'select' ? 'w-10 px-2' : undefined}
          onClick={
            cell.column.id === 'select' || cell.column.id === 'drag-handle'
              ? (event) => event.stopPropagation()
              : undefined
          }
        >
          {cell.column.id === 'drag-handle' ? (
            !isDraft ? (
              <div
                {...attributes}
                {...listeners}
                role="button"
                tabIndex={0}
                className="flex shrink-0 cursor-grab touch-none rounded p-1 hover:bg-muted active:cursor-grabbing"
                onClick={(e) => e.stopPropagation()}
              >
                <GripVertical className="h-4 w-4 text-muted-foreground" />
              </div>
            ) : null
          ) : (
            flexRender(cell.column.columnDef.cell, cell.getContext())
          )}
        </TableCell>
      ))}
    </TableRow>
  )
}

const SortableDataTable = ({ table, onDragEnd }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const rows = table.getRowModel().rows
  const sortableIds = useMemo(
    () =>
      rows.filter((row) => row.original.id !== 'draft').map((row) => row.id),
    [rows],
  )

  const visibleLength = useMemo(
    () =>
      table?.getAllLeafColumns()?.filter((column) => column.getIsVisible())
        ?.length,
    [table],
  )

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
    >
      <Table>
        <TableHeader className="sticky top-0 z-10 bg-background">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  colSpan={header.colSpan}
                  className={cn(
                    'bg-background',
                    header.column.id === 'z' ? 'text-left' : 'text-center',
                  )}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          <SortableContext
            items={sortableIds}
            strategy={verticalListSortingStrategy}
          >
            {rows.length ? (
              rows.map((row) => (
                <SortableRow
                  key={row.id}
                  row={row}
                  onRowClick={(targetRow) =>
                    targetRow.toggleSelected(!targetRow.getIsSelected())
                  }
                />
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={visibleLength}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </SortableContext>
        </TableBody>
      </Table>
    </DndContext>
  )
}

export default SortableDataTable
